import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CommonSectionActionsProps {
  isReadOnly: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
}

interface AmbienceSectionActionsProps extends CommonSectionActionsProps {
  mode: 'ambience';
  isLoadingSounds: boolean;
  hasValidationError: boolean;
  hasMissingSounds: boolean;
  disableReason: string | null;
}

interface NoiseSectionActionsProps extends CommonSectionActionsProps {
  mode: 'noise';
  hasValidationErrors: boolean;
  saveError: string | null;
}

type EditCampaignSectionActionsProps = AmbienceSectionActionsProps | NoiseSectionActionsProps;

interface CampaignSectionSaveActionsProps extends CommonSectionActionsProps {
  isSaveDisabled?: boolean;
}

export const CampaignSectionSaveActions: React.FC<CampaignSectionSaveActionsProps> = ({
  isReadOnly,
  isDirty,
  isSaving,
  isSaveDisabled = isReadOnly || !isDirty || isSaving,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();

  const labelSx = {
    display: 'block',
    height: 20,
    lineHeight: '20px',
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.02em',
  } as const;

  // Identical metrics on both buttons so label baselines match.
  const sharedSx = {
    height: 36,
    minHeight: 36,
    maxHeight: 36,
    py: 0,
    px: 2,
    m: 0,
    lineHeight: 1,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.02em',
    textTransform: 'none' as const,
    boxSizing: 'border-box' as const,
    border: '1px solid',
    boxShadow: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    '&:hover': { boxShadow: 'none' },
    '& .MuiButton-label, & > span': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '20px',
      height: 20,
    },
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
        height: 36,
      }}>
      <Button
        data-testid="campaign-section-save"
        variant="contained"
        color="primary"
        disableElevation
        disableRipple
        onClick={onSave}
        disabled={isSaveDisabled}
        sx={{
          ...sharedSx,
          borderColor: 'primary.main',
          '&.Mui-disabled': {
            borderColor: 'action.disabledBackground',
          },
        }}>
        {isSaving ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <Box component="span" sx={labelSx}>
            {t('action.save')}
          </Box>
        )}
      </Button>
      <Button
        data-testid="campaign-section-cancel"
        variant="contained"
        color="inherit"
        disableElevation
        disableRipple
        onClick={onCancel}
        disabled={isReadOnly || !isDirty || isSaving}
        sx={{
          ...sharedSx,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderColor: 'grey.400',
          '&:hover': {
            bgcolor: 'grey.50',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            bgcolor: 'background.paper',
            color: 'action.disabled',
            borderColor: 'action.disabled',
          },
        }}>
        <Box component="span" sx={labelSx}>
          {t('action.cancel')}
        </Box>
      </Button>
    </Box>
  );
};

export const EditCampaignSectionActions: React.FC<EditCampaignSectionActionsProps> = (props) => {
  const { t } = useTranslation();
  const { isReadOnly, isDirty, isSaving, onSave, onCancel } = props;
  const isSaveDisabled =
    isReadOnly ||
    !isDirty ||
    isSaving ||
    (props.mode === 'ambience' &&
      (props.isLoadingSounds || props.hasValidationError || props.hasMissingSounds));

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
        px: 4,
        pt: 3,
      }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" fontWeight={600}>
          {t(
            props.mode === 'ambience'
              ? 'campaignPage.ambienceSettings.title'
              : 'campaignPage.noiseProcessing.title',
          )}
        </Typography>
        {props.mode === 'ambience' ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {t('campaignPage.ambienceSettings.description')}
            </Typography>
            {props.disableReason ? (
              <Typography variant="body2" color="warning.main">
                {props.disableReason}
              </Typography>
            ) : null}
          </>
        ) : null}
        {props.mode === 'noise' && props.isReadOnly ? (
          <Typography variant="body2" color="warning.main">
            {t('campaignPage.noiseProcessing.disabledCampaign')}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <CampaignSectionSaveActions {...props} isSaveDisabled={isSaveDisabled} />
        {props.mode === 'ambience' && props.hasValidationError ? (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {t('campaignPage.ambienceSettings.probabilityTotalError')}
          </Typography>
        ) : null}
        {props.mode === 'ambience' && !props.hasValidationError && props.hasMissingSounds ? (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            {t('campaignPage.ambienceSettings.missingSoundWarning')}
          </Typography>
        ) : null}
        {props.mode === 'noise' && props.hasValidationErrors ? (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {t('campaignPage.noiseProcessing.validationSummary')}
          </Typography>
        ) : null}
        {props.mode === 'noise' && props.saveError ? (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {props.saveError}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
